import { oc } from "@orpc/contract";
import { z } from "zod";

const UUIDSchema = z.uuid();

export const CompanyBasicInfoSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

export const CompaniesBySupplierIdsInputSchema = z.object({
  supplierIds: z.array(UUIDSchema).min(1).max(500),
});

// Response is a record mapping supplierId -> company basic info (or null if no company found)
export const CompaniesBySupplierIdsResponseSchema = z.record(
  z.string(),
  CompanyBasicInfoSchema.nullable(),
);

export const CompanyIdInputSchema = z.object({
  companyId: z.uuid(),
});

export const AddSupplierResponseSchema = z.object({
  id: z.uuid(),
});

export const AddCustomerResponseSchema = z.object({
  id: z.uuid(),
});

const CompanyActionSuccessSchema = z.object({
  success: z.literal(true),
});

export const CompanyDistinctValuesInputSchema = z.object({
  column: z.string(),
  search: z.string().optional(),
});

export const CompanyDistinctValuesResponseSchema = z.object({
  values: z.array(z.string()),
});

export const companiesContracts = {
  bySupplierIds: oc
    .input(CompaniesBySupplierIdsInputSchema)
    .output(CompaniesBySupplierIdsResponseSchema)
    .route({ method: "GET" }),
  addSupplier: oc
    .input(CompanyIdInputSchema)
    .output(AddSupplierResponseSchema)
    .route({ method: "POST" }),
  removeSupplier: oc
    .input(CompanyIdInputSchema)
    .output(CompanyActionSuccessSchema)
    .route({ method: "POST" }),
  addCustomer: oc
    .input(CompanyIdInputSchema)
    .output(AddCustomerResponseSchema)
    .route({ method: "POST" }),
  removeCustomer: oc
    .input(CompanyIdInputSchema)
    .output(CompanyActionSuccessSchema)
    .route({ method: "POST" }),
  distinctValues: oc
    .input(CompanyDistinctValuesInputSchema)
    .output(CompanyDistinctValuesResponseSchema)
    .route({
      method: "GET",
      summary: "Get distinct values for a company column",
    }),
} as const;

export type CompanyBasicInfo = z.infer<typeof CompanyBasicInfoSchema>;
export type CompaniesBySupplierIdsInput = z.infer<
  typeof CompaniesBySupplierIdsInputSchema
>;
export type CompaniesBySupplierIdsResponse = z.infer<
  typeof CompaniesBySupplierIdsResponseSchema
>;
export type CompanyDistinctValuesInput = z.infer<
  typeof CompanyDistinctValuesInputSchema
>;
